import type { AgentSubscription } from "schemas/agent-billing";

import * as React from "react";

import { Button } from "@replohq/lattice/components/button/Button";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import { toast } from "@replohq/lattice/components/toast/Toast";
import {
  useCurrentWorkspaceId,
  useIsAgentBillingEnabled,
} from "@v2/contexts/WorkspaceDashboardContext";
import { useAgentBillingSubscription } from "@v2/features/agent-billing/hooks/useAgentSubscription";
import {
  agentBillingRouter,
  agentBillingUtils,
} from "@v2/trpc/routes/agentBilling";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { formatPrice } from "replo-utils/lib/format";

import { AddCreditsForm } from "./agent-billing/AddCreditsForm";
import {
  CapacityBalancesTable,
  CreditBalancesTable,
} from "./agent-billing/CreditBalancesTable";
import { CreditTransactionsTable } from "./agent-billing/CreditTransactionsTable";
import { RawRateCardsJson } from "./agent-billing/RawRateCardsJson";
import { RawSubscriptionJson } from "./agent-billing/RawSubscriptionJson";
import {
  DebugSection,
  MockCustomPricingToggle,
  StripeLink,
} from "./agent-billing/shared";
import { SubscriptionActions } from "./agent-billing/SubscriptionActions";
import { SubscriptionChangeLogsTable } from "./agent-billing/SubscriptionChangeLogsTable";
import { SubscriptionSummary } from "./agent-billing/SubscriptionSummary";

const AgentBillingEnabledToggle: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const isEnabled = useIsAgentBillingEnabled();
  const { mutateAsync: updateIsAgentBillingEnabled, isPending } =
    workspaceRouter.updateIsAgentBillingEnabled.useMutation();

  const handleToggle = async () => {
    if (!workspaceId) {
      return;
    }
    const nextEnabled = !isEnabled;
    await updateIsAgentBillingEnabled({
      workspaceId,
      enabled: nextEnabled,
    });
    window.location.reload();
  };

  if (!workspaceId) {
    return null;
  }

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-surface px-2.5 py-1.5">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs font-medium text-default">Agent Billing</span>
        <span className="text-xs text-muted">
          {isEnabled
            ? "Agent billing is enabled for this workspace."
            : "Enable agent billing for this workspace."}
        </span>
      </div>
      <Button
        size="sm"
        variant={isEnabled ? "destructive" : "secondary"}
        className="shrink-0"
        loading={isPending}
        onClick={() => void handleToggle()}
      >
        {isEnabled ? "Disable" : "Enable"}
      </Button>
    </div>
  );
};

const SyncWithStripeButton: React.FC<{
  stripeSubscriptionId: string;
}> = ({ stripeSubscriptionId }) => {
  const workspaceId = useCurrentWorkspaceId();

  const { mutateAsync: syncFromStripe, isPending } =
    agentBillingRouter.subscription.updateOrCreateFromStripeSubscription.useMutation(
      {
        onSuccess: () => {
          void agentBillingUtils.subscription.find.invalidate();
          void agentBillingUtils.subscription.findRateCards.invalidate();
        },
      },
    );

  const handleSync = async () => {
    if (!workspaceId) {
      return;
    }
    try {
      await syncFromStripe({
        workspaceId,
        stripeSubscriptionId,
      });
      toast.success({ title: "Subscription synced from Stripe" });
    } catch (error) {
      toast.error({
        title: "Failed to sync from Stripe",
        description: error instanceof Error ? error.message : String(error),
      });
    }
  };

  return (
    <Button
      variant="secondary"
      size="sm"
      loading={isPending}
      disabled={isPending}
      onClick={() => void handleSync()}
    >
      Sync with Stripe
    </Button>
  );
};

const FindAndSyncStripeSubscription: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const [foundSubscriptionId, setFoundSubscriptionId] = React.useState<
    string | null
  >(null);

  const {
    mutateAsync: findInStripe,
    isPending: isFinding,
    data: searchResult,
  } = agentBillingRouter.subscription.findStripeSubscription.useMutation();

  const handleFind = async () => {
    if (!workspaceId) {
      return;
    }
    try {
      const result = await findInStripe({ workspaceId });
      if (result?.stripeSubscriptionId) {
        setFoundSubscriptionId(result.stripeSubscriptionId);
        toast.success({
          title: `Found: ${result.stripeSubscriptionId} (${result.status})`,
        });
      } else {
        setFoundSubscriptionId(null);
        toast.error({
          title: "No active Stripe subscription found for this workspace",
        });
      }
    } catch (error) {
      toast.error({
        title: "Failed to search Stripe",
        description: error instanceof Error ? error.message : String(error),
      });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          loading={isFinding}
          disabled={isFinding}
          onClick={() => void handleFind()}
        >
          Find in Stripe
        </Button>
        {foundSubscriptionId && (
          <>
            <StripeLink value={foundSubscriptionId} />
            <SyncWithStripeButton stripeSubscriptionId={foundSubscriptionId} />
          </>
        )}
      </div>
      {searchResult === null && !isFinding && (
        <span className="text-muted text-xs">
          No active Stripe subscription with matching workspaceId metadata.
        </span>
      )}
    </div>
  );
};

const SubscriptionItemsView: React.FC<{
  activeSubscription: AgentSubscription;
}> = ({ activeSubscription }) => {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <StripeLink value={activeSubscription.stripeSubscriptionId} />
        <SyncWithStripeButton
          stripeSubscriptionId={activeSubscription.stripeSubscriptionId}
        />
      </div>
      <div className="flex flex-col gap-2">
        {activeSubscription.items.map((subscriptionItem) => (
          <div
            key={subscriptionItem.id}
            className="flex flex-col gap-0.5 rounded-md border border-border bg-surface px-2.5 py-1.5"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs font-semibold">
                {subscriptionItem.productType}
              </span>
              <span className="font-mono text-xs">
                qty: {subscriptionItem.quantity}
              </span>
            </div>
            <div className="flex items-center justify-between text-muted">
              <StripeLink value={subscriptionItem.stripePriceId} />
              <span className="font-mono text-xs">
                {formatPrice({
                  amount: subscriptionItem.unitAmount,
                  currency: subscriptionItem.currency,
                })}
                /{activeSubscription.interval === "year" ? "yr" : "mo"} each
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AgentBillingTab: React.FC = () => {
  const { data: activeSubscription } = useAgentBillingSubscription();

  return (
    <div className="flex flex-col">
      <DebugSection title="Options">
        <AgentBillingEnabledToggle />
        <MockCustomPricingToggle />
      </DebugSection>
      <Separator />
      <DebugSection title="Subscription Summary">
        <SubscriptionSummary />
      </DebugSection>
      <Separator />
      <DebugSection title="Subscription Actions">
        <SubscriptionActions />
      </DebugSection>
      <Separator />
      <DebugSection title="Subscription Items">
        {activeSubscription ? (
          <SubscriptionItemsView activeSubscription={activeSubscription} />
        ) : (
          <div className="flex flex-col gap-2">
            <span className="text-muted text-xs">
              No subscription in DB. Use the customer-facing plan selector,
              create one in Stripe, or find an existing one:
            </span>
            <FindAndSyncStripeSubscription />
          </div>
        )}
      </DebugSection>
      <Separator />
      <DebugSection title="Capacity">
        <CapacityBalancesTable />
      </DebugSection>
      <Separator />
      <DebugSection title="Credits">
        <CreditBalancesTable />
        <AddCreditsForm />
      </DebugSection>
      <Separator />
      <DebugSection title="Credit Transactions">
        <CreditTransactionsTable />
      </DebugSection>
      <Separator />
      <DebugSection title="Subscription Change Log">
        <SubscriptionChangeLogsTable />
      </DebugSection>
      <Separator />
      <DebugSection title="Raw Subscription JSON">
        <RawSubscriptionJson />
      </DebugSection>
      <Separator />
      <DebugSection title="Raw Rate Cards JSON">
        <RawRateCardsJson />
      </DebugSection>
    </div>
  );
};
